import { useMemo, useState } from 'react';
import { Button, Card } from '@playerbase/ui';

import { getEdgeFunctionsBaseUrl } from '../../../lib/edge-functions-url.js';
import { downloadSnippet } from './download-snippet.js';
import { SnippetBlock, type SnippetLang } from './snippet-block.js';
import { buildPairGodotSnippet } from './snippet-pair-godot.js';
import { buildPairHttpSnippet } from './snippet-pair-http.js';
import { buildPairUnitySnippet } from './snippet-pair-unity.js';
import { buildPairUnrealSnippet } from './snippet-pair-unreal.js';
import { buildPairWebSnippet } from './snippet-pair-web.js';

type Lang = 'godot' | 'unity' | 'unreal' | 'web' | 'http';

const LANG_LABEL: Record<Lang, string> = {
  godot: 'Godot (GDScript)',
  unity: 'Unity (C#)',
  unreal: 'Unreal (C++)',
  web: 'Web (JavaScript)',
  http: 'Raw HTTP',
};

const SNIPPET_LANG: Record<Lang, SnippetLang> = {
  godot: 'gdscript',
  unity: 'csharp',
  unreal: 'cpp',
  web: 'javascript',
  http: 'http',
};

const PAIR_FILENAME: Record<Lang, string> = {
  godot: 'player_base_pairing.gd',
  unity: 'PlayerBasePairing.cs',
  unreal: 'PlayerBasePairing.cpp',
  web: 'player-base-pairing.js',
  http: 'playerbase-pair.sh',
};

interface PairingIntegrationCardProps {
  // The full plaintext key, set transiently after a rotate in the
  // ApiKeyCard above. While non-null, the snippet inlines the real key
  // so studios can copy a runnable version.
  plaintext: string | null;
}

export function PairingIntegrationCard({
  plaintext,
}: PairingIntegrationCardProps): JSX.Element {
  const [lang, setLang] = useState<Lang>('godot');
  const [copied, setCopied] = useState(false);

  const keyForSnippet = useMemo(() => {
    if (plaintext !== null) return plaintext;
    return 'YOUR_PLAYERBASE_API_KEY';
  }, [plaintext]);

  const functionsBaseUrl = useMemo(() => getEdgeFunctionsBaseUrl(), []);

  const snippet = useMemo(() => {
    if (lang === 'godot') return buildPairGodotSnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'unity') return buildPairUnitySnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'unreal') return buildPairUnrealSnippet(keyForSnippet, functionsBaseUrl);
    if (lang === 'web') return buildPairWebSnippet(keyForSnippet, functionsBaseUrl);
    return buildPairHttpSnippet(keyForSnippet, functionsBaseUrl);
  }, [lang, keyForSnippet, functionsBaseUrl]);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can throw inside iframes / on insecure contexts. The
      // snippet stays visible for hand-selection in that case.
    }
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title>Pairing</Card.Title>
        <Card.Description>
          Drop this into your game once. It redeems the pairing code the
          bot DMs your testers and stores a durable player token the event
          snippet below reuses on every call.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <div role="tablist" aria-label="Snippet language" className="flex gap-2 border-b border-border">
          {(['godot', 'unity', 'unreal', 'web', 'http'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={lang === value}
              onClick={() => setLang(value)}
              className={
                lang === value
                  ? 'border-b-2 border-accent px-3 py-1.5 text-sm text-text'
                  : 'border-b-2 border-transparent px-3 py-1.5 text-sm text-text-muted hover:text-text'
              }
            >
              {LANG_LABEL[value]}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => downloadSnippet(PAIR_FILENAME[lang], snippet)}
          >
            Download {PAIR_FILENAME[lang]}
          </Button>
          <Button variant="ghost" onClick={() => void handleCopy()}>
            {copied ? 'Copied!' : 'Copy snippet'}
          </Button>
        </div>

        <SnippetBlock code={snippet} lang={SNIPPET_LANG[lang]} />
      </Card.Content>
    </Card>
  );
}
